import { forwardRef, useImperativeHandle, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import type { RequestFormSchema } from "@/types/requestForm"

import { fieldRegistry, type FieldRendererProps } from "./fieldRegistry"

export interface SchemaFormViewProps {
  schema?: RequestFormSchema
  mode?: "preview" | "runtime"
  values?: Record<string, unknown>
  onFieldChange?: (name: string, value: unknown) => void
  onReject?: () => void
  busy?: boolean
  contextId?: string // Unique identifier to prevent React from reusing component instances across different contexts
}

export interface SchemaFormViewHandle {
  validate: () => boolean
}

export const SchemaFormView = forwardRef<
  SchemaFormViewHandle,
  SchemaFormViewProps
>(
  (
    {
      schema,
      mode = "preview",
      values,
      onFieldChange,
      onReject,
      busy,
      contextId,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const [errors, setErrors] = useState<Record<string, string | undefined>>({})

    const validateAll = useMemo(() => {
      return () => {
        if (!schema) return true
        const nextErrors: Record<string, string | undefined> = {}
        for (const f of schema.fields) {
          const spec = fieldRegistry[f.type]
          const validator = spec?.validator
          if (!validator) continue
          const err = validator(f, values ? values[f.name] : undefined, t)
          if (err) nextErrors[f.name] = err
        }
        setErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
      }
    }, [schema, values, t])

    useImperativeHandle(ref, () => ({
      validate: validateAll,
    }))

    if (!schema) {
      return (
        <div className="text-xs text-muted-foreground">
          {t("workspace.agent.basicForm.formBuilder.preview.noForm")}
        </div>
      )
    }
    return (
      <div className="space-y-3">
        {mode === "preview" && (
          <div className="text-sm font-medium">
            {schema.title ||
              t("workspace.agent.basicForm.formBuilder.preview.untitled")}
          </div>
        )}
        {schema.description ? (
          <div className="text-xs text-muted-foreground whitespace-pre-wrap">
            {schema.description}
          </div>
        ) : null}
        <div className="space-y-3">
          {schema.fields.map((f) => {
            const Comp = fieldRegistry[f.type]?.renderer
            if (!Comp) return null
            return (
              <div key={`${contextId || "default"}-${f.id}`}>
                <Comp
                  field={f as unknown as FieldRendererProps["field"]}
                  mode={mode}
                  value={
                    values && values[f.name] !== undefined
                      ? (values[f.name] as
                          | string
                          | number
                          | boolean
                          | string[]
                          | undefined)
                      : undefined
                  }
                  onChange={(v) => onFieldChange?.(f.name, v)}
                  disabled={busy}
                  error={errors?.[f.name]}
                  contextId={contextId}
                />
              </div>
            )
          })}
        </div>
        {mode === "preview" ? (
          <TooltipProvider>
            <div className="pt-2 flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled
                      aria-disabled="true"
                      className="cursor-not-allowed opacity-60"
                    >
                      {t(
                        "workspace.agent.basicForm.formBuilder.preview.reject"
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t(
                    "workspace.agent.basicForm.formBuilder.preview.disabledActions"
                  )}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled
                      aria-disabled="true"
                      className="cursor-not-allowed opacity-60"
                    >
                      {t(
                        "workspace.agent.basicForm.formBuilder.preview.submit"
                      )}
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t(
                    "workspace.agent.basicForm.formBuilder.preview.disabledActions"
                  )}
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        ) : (
          <div className="pt-2 flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onReject}
              disabled={busy}
            >
              {t("workspace.agent.basicForm.formBuilder.preview.reject")}
            </Button>
            <Button type="submit" disabled={busy}>
              {t("workspace.agent.basicForm.formBuilder.preview.submit")}
            </Button>
          </div>
        )}
      </div>
    )
  }
)

export default SchemaFormView
